import React, { useState } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';

function MediaModal({ chatId, onClose }) {
  const [file, setFile] = useState(null);
  const [previewURL, setPreviewURL] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);

  function pickFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewURL(URL.createObjectURL(f));
  }

  function onDrop(e) {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewURL(URL.createObjectURL(f));
  }

  function onDragOver(e) {
    e.preventDefault();
  }

  async function sendImage() {
    if (!file) return;
    setUploading(true);

    try {
      const fd = new FormData();
      fd.append('image', file, file.name);
      if (caption?.trim()) fd.append('caption', caption.trim());

      const resp = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/chats/${chatId}/media`,
        { method: 'POST', body: fd }
      );

      if (!resp.ok) {
        const err = await resp.text();
        console.error('Upload failed:', err);
        alert('Error al enviar la imagen. Por favor intenta de nuevo.');
      }

      onClose();
    } catch (err) {
      console.error('Upload error:', err);
      alert('Error de conexión. Por favor intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 animate-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-gradient-to-r from-brand-primary/5 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-primary/10 rounded-xl">
              <ImageIcon className="w-5 h-5 text-brand-primary" />
            </div>
            <h3 className="text-lg font-extrabold text-slate-900 font-montserrat">
              Enviar Imagen
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-xl transition-all text-slate-500 hover:text-slate-900"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Drop Zone */}
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            className={`
              border-2 border-dashed rounded-2xl p-8 text-center 
              transition-all duration-200
              ${previewURL 
                ? 'border-brand-primary bg-brand-primary/5' 
                : 'border-slate-300 bg-slate-50 hover:border-brand-primary hover:bg-white'
              }
            `}
          >
            {previewURL ? (
              <div className="relative">
                <img
                  src={previewURL}
                  alt="Preview"
                  className="max-w-full max-h-80 object-contain mx-auto rounded-xl shadow-md"
                />
                <button
                  onClick={() => {
                    setFile(null);
                    setPreviewURL('');
                  }}
                  className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="py-12">
                <Upload className="w-16 h-16 text-brand-primary mx-auto mb-4" />
                <div className="text-slate-900 mb-2 font-bold font-montserrat">
                  Arrastra una imagen aquí
                </div>
                <div className="text-sm text-slate-500 font-montserrat">
                  o haz clic abajo para seleccionar
                </div>
              </div>
            )}
            <div className="mt-4">
              <label className="inline-flex items-center gap-2 cursor-pointer px-6 py-2.5 bg-gradient-to-r from-brand-primary to-brand-primary/90 text-white rounded-xl font-bold hover:from-brand-primary/90 hover:to-brand-primary hover:scale-105 transition-all shadow-md font-montserrat">
                <ImageIcon className="w-4 h-4" />
                Elegir archivo
                <input
                  type="file"
                  accept="image/*"
                  onChange={pickFile}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Caption Input */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2 font-montserrat">
              Descripción (opcional)
            </label>
            <input
              type="text"
              placeholder="Agrega una descripción..."
              value={caption}
              onChange={e => setCaption(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 text-slate-900 rounded-xl outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 transition-all font-montserrat placeholder-slate-400"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={uploading}
              className="flex-1 px-4 py-3 bg-white border-2 border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all font-semibold font-montserrat disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={sendImage}
              disabled={!file || uploading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-brand-green to-brand-green/90 text-white rounded-xl font-bold hover:from-brand-green/90 hover:to-brand-green hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-md font-montserrat"
            >
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Enviando...
                </span>
              ) : (
                'Enviar imagen'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MediaModal;